import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IoAddOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoRefreshOutline,
  IoRemoveOutline,
} from 'react-icons/io5';
import type { AlignedPoint } from '../../utils/comparison/alignStreams';
import type { ComparisonAlignment, ComparisonMetric } from '../../utils/comparison/alignStreams';
import { COMPARISON_COLORS } from './types';
import { useChartScrub } from '../../hooks/useChartScrub';
import {
  buildAlignedMetricPath,
  buildDualChartLayout,
  chartXFromDataX,
  dataXFromClientX,
  formatChartX,
  formatMetricValue,
  nearestAlignedIndex,
} from './chartHoverUtils';
import { ChartHoverHeader, ChartHoverRow, ChartHoverTooltip } from './ChartHoverTooltip';

interface DualStreamChartProps {
  running?: boolean;
  aligned: AlignedPoint[];
  metric: ComparisonMetric;
  alignment: ComparisonAlignment;
  labelA: string;
  labelB: string;
  height?: number;
}

interface ZoomWindow {
  start: number;
  end: number;
}

const MIN_ZOOM_SPAN = 0.125;
const FULL_ZOOM: ZoomWindow = { start: 0, end: 1 };

const METRIC_UNITS: Record<ComparisonMetric, string> = {
  power: 'W',
  heart_rate: 'bpm',
  cadence: 'rpm',
  speed: 'km/h',
  altitude: 'm',
};

export function resizeZoomWindow(current: ZoomWindow, scale: number): ZoomWindow {
  const currentSpan = current.end - current.start;
  const nextSpan = Math.min(1, Math.max(MIN_ZOOM_SPAN, currentSpan * scale));
  const center = (current.start + current.end) / 2;
  let start = center - nextSpan / 2;
  let end = center + nextSpan / 2;

  if (start < 0) {
    end -= start;
    start = 0;
  }
  if (end > 1) {
    start -= end - 1;
    end = 1;
  }

  return { start: Math.max(0, start), end: Math.min(1, end) };
}

export function panZoomWindow(current: ZoomWindow, direction: -1 | 1): ZoomWindow {
  const span = current.end - current.start;
  const delta = span * 0.4 * direction;
  const start = Math.min(1 - span, Math.max(0, current.start + delta));
  return { start, end: start + span };
}

export function DualStreamChart({
  running = false,
  aligned,
  metric,
  alignment,
  labelA,
  labelB,
  height = 280,
}: DualStreamChartProps) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [zoomWindow, setZoomWindow] = useState<ZoomWindow>(FULL_ZOOM);

  useEffect(() => {
    setZoomWindow(FULL_ZOOM);
    setHoverIdx(null);
  }, [aligned, metric, alignment]);

  const fullXMax = useMemo(
    () => aligned.reduce((max, point) => Math.max(max, point.x), 0),
    [aligned],
  );
  const zoomSpan = zoomWindow.end - zoomWindow.start;
  const isZoomed = zoomSpan < 0.999;
  const zoomPercent = Math.round(100 / zoomSpan);

  const { visibleAligned, visibleStartX } = useMemo(() => {
    if (!isZoomed || fullXMax <= 0) {
      return { visibleAligned: aligned, visibleStartX: 0 };
    }

    const startX = fullXMax * zoomWindow.start;
    const endX = fullXMax * zoomWindow.end;
    const visible = aligned
      .filter(point => point.x >= startX && point.x <= endX)
      .map(point => ({ ...point, x: point.x - startX }));

    return {
      visibleAligned: visible.length ? visible : aligned.slice(0, 1).map(point => ({ ...point, x: 0 })),
      visibleStartX: startX,
    };
  }, [aligned, fullXMax, isZoomed, zoomWindow]);

  const layout = useMemo(() => buildDualChartLayout(visibleAligned, height), [visibleAligned, height]);

  const { pathA, pathB } = useMemo(() => {
    if (!visibleAligned.length) return { pathA: '', pathB: '' };
    return {
      pathA: buildAlignedMetricPath(visibleAligned, 'valueA', layout),
      pathB: buildAlignedMetricPath(visibleAligned, 'valueB', layout),
    };
  }, [visibleAligned, layout]);

  const updateHoverAtX = useCallback(
    (clientX: number) => {
      const svg = wrapRef.current?.querySelector('svg');
      if (!svg) return;
      const dataX = dataXFromClientX(svg, clientX, layout);
      if (dataX == null) {
        setHoverIdx(null);
        return;
      }
      setHoverIdx(nearestAlignedIndex(visibleAligned, dataX));
    },
    [visibleAligned, layout],
  );

  const scrub = useChartScrub(true, updateHoverAtX, () => setHoverIdx(null));

  const hoverPoint = hoverIdx != null ? visibleAligned[hoverIdx] : null;
  const hoverLineX = hoverPoint ? chartXFromDataX(hoverPoint.x, layout) : null;

  const tooltipPos = useMemo(() => {
    if (hoverLineX == null || !wrapRef.current) return null;
    const w = wrapRef.current.clientWidth;
    const scale = w / 800;
    const px = hoverLineX * scale;
    const tooltipW = 168;
    const left = px + 14 + tooltipW > w ? px - tooltipW - 14 : px + 14;
    return { left: Math.max(4, left), top: 28 };
  }, [hoverLineX]);

  const zoomIn = useCallback(() => setZoomWindow(current => resizeZoomWindow(current, 0.6)), []);
  const zoomOut = useCallback(() => setZoomWindow(current => resizeZoomWindow(current, 1 / 0.6)), []);
  const resetZoom = useCallback(() => setZoomWindow(FULL_ZOOM), []);
  const panPrevious = useCallback(() => setZoomWindow(current => panZoomWindow(current, -1)), []);
  const panNext = useCallback(() => setZoomWindow(current => panZoomWindow(current, 1)), []);

  if (!aligned.length) {
    return (
      <div className="cmp-dual-chart cmp-dual-chart--empty" style={{ ...chartWrap, minHeight: height }}>
        {t('compare.chart.empty')}
      </div>
    );
  }

  const xAxisLabel = alignment === 'distance' ? t('compare.alignDistance') : t('compare.alignTime');
  const metricLabel = t(metric === 'cadence' && running ? 'runningMetrics.cadence' : `compare.mobile.metric.${metric}`);
  const originalHoverX = hoverPoint ? hoverPoint.x + visibleStartX : null;

  return (
    <div className="cmp-dual-chart" style={chartWrap}>
      <div className="cmp-dual-chart__topbar">
        <div className="cmp-dual-chart__label">{metricLabel} · {xAxisLabel}</div>
        <div className="cmp-dual-chart__zoom" role="group" aria-label={t('compare.chart.zoomControls')} dir="ltr">
          <button type="button" onClick={panPrevious} disabled={!isZoomed || zoomWindow.start <= 0} aria-label={t('compare.chart.panPrevious')} title={t('compare.chart.panPrevious')}>
            <IoChevronBackOutline aria-hidden />
          </button>
          <button type="button" onClick={zoomOut} disabled={!isZoomed} aria-label={t('compare.chart.zoomOut')} title={t('compare.chart.zoomOut')}>
            <IoRemoveOutline aria-hidden />
          </button>
          <span title={t('compare.chart.zoomLevel', { value: zoomPercent })}>{zoomPercent}%</span>
          <button type="button" onClick={zoomIn} disabled={zoomSpan <= MIN_ZOOM_SPAN + 0.001} aria-label={t('compare.chart.zoomIn')} title={t('compare.chart.zoomIn')}>
            <IoAddOutline aria-hidden />
          </button>
          <button type="button" onClick={panNext} disabled={!isZoomed || zoomWindow.end >= 1} aria-label={t('compare.chart.panNext')} title={t('compare.chart.panNext')}>
            <IoChevronForwardOutline aria-hidden />
          </button>
          {isZoomed && (
            <button type="button" onClick={resetZoom} aria-label={t('compare.chart.zoomReset')} title={t('compare.chart.zoomReset')}>
              <IoRefreshOutline aria-hidden />
            </button>
          )}
        </div>
      </div>

      <div ref={wrapRef} className="comparison-chart-touch cmp-dual-chart__plot" dir="ltr">
        <svg
          viewBox={`0 0 800 ${height}`}
          width="100%"
          height={height - 24}
          preserveAspectRatio="none"
          {...scrub}
          style={{ display: 'block' }}
        >
          <rect x={0} y={0} width={800} height={height} fill="var(--palette-sunken)" rx={8} />
          {pathA && <path d={pathA} fill="none" stroke={COMPARISON_COLORS.a} strokeWidth={2} opacity={0.95} />}
          {pathB && <path d={pathB} fill="none" stroke={COMPARISON_COLORS.b} strokeWidth={2} strokeDasharray="7 4" opacity={0.95} />}
          {hoverLineX != null && (
            <line
              x1={hoverLineX}
              y1={layout.mt}
              x2={hoverLineX}
              y2={layout.mt + layout.plotH}
              stroke="rgba(255,255,255,.22)"
              strokeWidth={1}
            />
          )}
        </svg>

        {hoverPoint && tooltipPos && originalHoverX != null && (
          <ChartHoverTooltip left={tooltipPos.left} top={tooltipPos.top} width={168}>
            <ChartHoverHeader>
              {xAxisLabel}: {formatChartX(originalHoverX, alignment)}
            </ChartHoverHeader>
            <ChartHoverRow
              label={labelA}
              value={hoverPoint.valueA != null ? `${formatMetricValue(metric, hoverPoint.valueA)} ${metric === 'cadence' && running ? t('runningMetrics.cadenceUnit') : METRIC_UNITS[metric]}` : '—'}
              color={COMPARISON_COLORS.a}
            />
            <ChartHoverRow
              label={labelB}
              value={hoverPoint.valueB != null ? `${formatMetricValue(metric, hoverPoint.valueB)} ${metric === 'cadence' && running ? t('runningMetrics.cadenceUnit') : METRIC_UNITS[metric]}` : '—'}
              color={COMPARISON_COLORS.b}
            />
          </ChartHoverTooltip>
        )}
      </div>

      <div className="cmp-dual-chart__legend">
        <span style={{ color: COMPARISON_COLORS.a }}><i aria-hidden />{labelA}</span>
        <span style={{ color: COMPARISON_COLORS.b }}><i aria-hidden />{labelB}</span>
      </div>
    </div>
  );
}

const chartWrap: React.CSSProperties = {
  background: 'var(--surface-0)',
  borderRadius: 'var(--r-3)',
  boxShadow: 'var(--nm-raised-sm)',
  padding: 12,
};


